/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { isPresent } from '@ember/utils';
import DS from 'ember-data';
import { sanitize, sanitizeWithModifier } from 'cio/utils/sanitize';
import ActionModel from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import LayoutModel from 'cio/models/layout';
import { config } from 'cio/helpers/preview-whitelist';

const CONTENT_KEY_PATTERN = /{{\s*content\s*}}/i;

interface MessageActionContentPreviewArgs {
  action: ActionModel;
  disabled: boolean;
  disableOverlays?: boolean;
  disableFrameScrolling?: boolean;
  animateFrameLoad?: boolean;
  contentBodyModifier?: (body: HTMLElement) => HTMLElement;
  previewClassNames?: string;
  frameClassNames?: string;
  hasReadOnlyPermission: boolean;
}

export default class MessageActionContentPreview extends Component<MessageActionContentPreviewArgs> {
  @service store!: DS.Store;

  @tracked template: TemplateModel | null = null;
  @tracked layout: LayoutModel | null = null;
  @tracked fadeInPreview = false;

  fetchTemplateTask = dropTask(async () => {
    let templateRef = this.args.action.belongsTo('template');
    if (templateRef.id() == null) {
      return;
    }

    let template = templateRef.value() as TemplateModel;
    if (template == null) {
      template = await templateRef.reload();
    }

    // should already be loaded with template request
    this.layout = await template.belongsTo('layout').load();

    this.template = template;
  });

  @computed(
    'args.action.{hasNoContent,hasNoRequest,collection,attributeName}',
    'args.action.info.{showNoContent,showNoRequest,showNoQuery}',
  )
  get hasContent() {
    if (this.args.action.info.showNoContent) {
      return !this.args.action.hasNoContent;
    }
    if (this.args.action.info.showRequest) {
      return !this.args.action.hasNoRequest;
    }
    if (this.args.action.info.showNoQuery) {
      return isPresent(this.args.action.collection) && isPresent(this.args.action.attributeName);
    }

    return true;
  }

  @computed('args.template.body')
  get sanitizedBody() {
    if (this.template == null) {
      return '';
    }

    let { body } = this.template;
    if (isPresent(this.layout?.body)) {
      let matches = CONTENT_KEY_PATTERN.exec(this.layout!.body);
      if (matches != null) {
        let [contentKey] = matches;
        body = this.layout!.body.replace(contentKey, body);
      }
    }

    if (this.args.contentBodyModifier != null) {
      return sanitizeWithModifier(body, config, this.args.contentBodyModifier);
    }

    return sanitize(body, config);
  }

  @action
  onFrameLoad() {
    if (!this.args.animateFrameLoad) {
      return;
    }

    this.fadeInPreview = true;
  }
}
